import { api } from '../api/client';

import { API_DASHBOARDS_URL } from '../utils';
import { withElementChoicesField } from '../utils/elementRefusal';

/**
 * Dashboards (keepr-api docs/SCHEMA.md "Charts" › "Dashboards"; plan
 * docs/plans/analytics.md §4.5, §4.7, §6.5; charts A6).
 *
 * A dashboard is a named arrangement of saved charts in one collection, with
 * one date range and one filter over every chart on it. It is a sibling of a
 * saved chart (services/charts.js): the same two tiers — `scope:
 * 'collection'` (pinned for everyone, a manager's) or `scope: 'user'` (the
 * caller's own) — the same names, caps, History and "one answer" for gone,
 * not yours and unreadable (a 404, like a made-up id). A sub-collection
 * inherits no dashboard from the collections above it (KPR-1084).
 *
 * Dashboard wire shape (GET one):
 *   { _id, collection_id, scope, userId, name, description, version,
 *     controls: { range: { preset } | { from, to } | null, filter: '' | text },
 *     tiles: [
 *       { id, kind: 'chart', chart_id, w, h, available, chart? },
 *       { id, kind: 'text', markdown, w, h: 1 },
 *     ],
 *     canEdit, createdBy, createdAt, updatedBy, updatedAt }
 *
 * The list's rows carry `tileCount` instead of `tiles`. A chart tile the
 * caller may not see here reads `available: false` and nothing else — ONE
 * state for deleted, made personal, unreadable and a card that does not
 * reach here. `canEdit` says whether THIS caller may change (and Arrange) it.
 *
 * ARRANGE IS A DRAFT SAVED ON DONE (ruled d17): the page saves the whole
 * layout in one `updateDashboard(…, { tiles, controls?, version })`; a save
 * that states an older version is 409 `version_conflict` with `version` (the
 * current one) and `dashboard` (as the caller now sees it), and the arranger
 * chooses — load theirs, or save theirs over it with that version.
 */

export const DASHBOARD_SCOPE = Object.freeze({ COLLECTION: 'collection', USER: 'user' });

/** The most tiles a dashboard holds (⚑ d12; 400 `too_many_tiles` past it). */
export const MAX_TILES = 24;

const dashboardUrl = (collectionId, dashboardId) => `${API_DASHBOARDS_URL(collectionId)}/${dashboardId}`;

/**
 * The collection's own dashboards — the collection tier first, then the
 * caller's own, by name within each. The API's `inherited` is always empty
 * since KPR-1084 and is not read.
 */
export async function getDashboardLists(collectionId) {
	const { data } = await api.get(API_DASHBOARDS_URL(collectionId));
	return { dashboards: Array.isArray(data?.dashboards) ? data.dashboards : [] };
}

/** One dashboard, its tiles judged for the caller here (404 for anything they may not see). */
export async function getDashboard(collectionId, dashboardId) {
	const { data } = await api.get(dashboardUrl(collectionId, dashboardId));
	return data;
}

/**
 * Tiles as a save sends them: the layout only, never the read side
 * (`available`, `chart`), which the server would refuse as unknown keys.
 */
export function tilesForSave(tiles) {
	return (Array.isArray(tiles) ? tiles : []).filter(Boolean).map((tile) => (tile.kind === 'text'
		? { id: tile.id, kind: 'text', markdown: tile.markdown, w: tile.w, h: 1 }
		: { id: tile.id, kind: 'chart', chart_id: tile.chart_id, w: tile.w, h: tile.h }));
}

/**
 * Always creates a PERSONAL dashboard (pinning it for everyone is promote's
 * job). `description`, `tiles` and `controls` are sent only when given.
 */
export async function createDashboard(collectionId, { name, description, tiles, controls, elementChoices = null } = {}) {
	const payload = { name };
	if (typeof description === 'string' && description.trim()) payload.description = description;
	if (Array.isArray(tiles)) payload.tiles = tilesForSave(tiles);
	if (controls && typeof controls === 'object') payload.controls = controls;
	// The person's answer to an ambiguous element name in the filter (utils/elementRefusal.js).
	const { data } = await api.post(API_DASHBOARDS_URL(collectionId), withElementChoicesField(payload, payload.controls ? elementChoices : null));
	return data;
}

/**
 * A partial update: only the keys given are sent. `description: ''` clears
 * it (sent as null). `tiles` is the whole layout and must carry the
 * `version` it was arranged from; a rename alone may leave it out.
 */
export async function updateDashboard(collectionId, dashboardId, patch = {}) {
	const payload = {};
	if (patch.name !== undefined) payload.name = patch.name;
	if (patch.description !== undefined) payload.description = patch.description === '' ? null : patch.description;
	if (patch.tiles !== undefined) payload.tiles = tilesForSave(patch.tiles);
	if (patch.controls !== undefined) payload.controls = patch.controls;
	if (patch.version !== undefined) payload.version = patch.version;
	const { data } = await api.patch(dashboardUrl(collectionId, dashboardId), withElementChoicesField(payload, payload.controls ? patch.elementChoices : null));
	return data;
}

/** Hard delete, restorable from History. Its charts stay. */
export async function deleteDashboard(collectionId, dashboardId) {
	await api.delete(dashboardUrl(collectionId, dashboardId));
}

/** manage — "Pin for everyone": the dashboard moves to the collection tier (409 `personal_tiles` while it holds a personal chart). */
export async function promoteDashboard(collectionId, dashboardId) {
	const { data } = await api.post(`${dashboardUrl(collectionId, dashboardId)}/promote`);
	return data;
}

/** manage — "Unpin — return to your own list": the dashboard becomes the CALLER's personal dashboard. */
export async function demoteDashboard(collectionId, dashboardId) {
	const { data } = await api.post(`${dashboardUrl(collectionId, dashboardId)}/demote`);
	return data;
}

/** The 409 of a stale save, read: `{ version, dashboard }`, or null for any other failure. */
export function versionConflictOf(error) {
	const body = error?.response?.data;
	if (error?.response?.status !== 409 || body?.code !== 'version_conflict') return null;
	return {
		version: Number.isInteger(body.version) ? body.version : null,
		dashboard: body.dashboard && typeof body.dashboard === 'object' ? body.dashboard : null,
	};
}

/** The tile a refused save names (`tiles[3].chart_id` → 3), or null. */
export function refusedTileIndex(error) {
	const path = error?.response?.data?.path;
	const m = typeof path === 'string' ? /^tiles\[(\d+)\]/.exec(path) : null;
	return m ? Number(m[1]) : null;
}

/** The sentence for a refused dashboard WRITE (rename, delete, pin, save): the server's own words where it gave them. */
export function dashboardRefusal(error, fallback) {
	const status = error?.response?.status;
	const body = error?.response?.data || {};
	if (status === 404) return 'This dashboard is no longer available.';
	if (typeof body.message === 'string' && body.message.trim() && status && status < 500) return body.message.trim();
	if (status === 503) return 'keepr is busy right now. Try again in a few seconds.';
	return fallback;
}
